/**
 * Interroge les doublons pendant la saisie.
 *
 * - Pas à chaque frappe : une pause de 400 ms, pour ne pas inonder l'API quand on tape un numéro de série.
 * - Une réponse tardive d'une saisie périmée est jetée : l'avertissement ne montre jamais les doublons
 *   d'un numéro qu'on a déjà corrigé.
 * - Un échec est silencieux : cette lecture n'est qu'un confort, le serveur refait le contrôle à
 *   l'enregistrement et c'est LUI qui arrête une création non confirmée.
 *
 * `interroger` vaut `null` quand rien n'identifie encore la fiche (aucun numéro, aucun nom) : on ne
 * demande rien. `cle` résume la saisie qui compte : la recherche repart quand elle change.
 */
import { useEffect, useRef, useState } from 'react';

import type { Doublon } from '../domaine/doublons';

const PAUSE_MS = 400;

export function useDoublons(
  interroger: (() => Promise<readonly Doublon[]>) | null,
  cle: string,
): readonly Doublon[] {
  const [doublons, setDoublons] = useState<readonly Doublon[]>([]);
  const derniere = useRef(interroger);
  derniere.current = interroger;
  const actif = interroger !== null;

  useEffect(() => {
    if (!actif) {
      setDoublons([]);
      return undefined;
    }
    let periimee = false;
    const minuterie = window.setTimeout(() => {
      const appel = derniere.current;
      if (!appel) return;
      appel()
        .then((liste) => {
          if (!periimee) setDoublons(liste);
        })
        .catch(() => {
          if (!periimee) setDoublons([]);
        });
    }, PAUSE_MS);
    return () => {
      periimee = true;
      window.clearTimeout(minuterie);
    };
  }, [actif, cle]);

  return doublons;
}
